"use client";

import { clsx } from "clsx";
import type { ReactNode } from "react";

import type { usePreviewPlayer } from "@ui/components/core/PreviewPlayer/usePreviewPlayer";
import { MediaRow } from "./MediaRow";
import { VoicePreviewAvatar } from "./VoicePreviewAvatar";

interface VoiceMediaRowProps {
  /** Avatar shown at rest; swapped for the preview toggle on hover/focus/play. */
  avatar: ReactNode;
  /** Voice display name. */
  name: string;
  /**
   * Right-aligned content — typically the use-voice action and the meta/descriptor text, which fade
   * in/out on hover. Use {@link voiceRowActionClassName} / {@link voiceRowMetaClassName} for the
   * shared swap styling and add any per-row visible/hidden state.
   */
  children: ReactNode;
  /** Whether this row has a playable preview (gates the preview button and the row click target). */
  hasPreview: boolean;
  isPlaying: boolean;
  isLoading: boolean;
  progressRingProps: ReturnType<typeof usePreviewPlayer>["progressRingProps"];
  /** Active row — keeps the preview button shown and the avatar faded even when not hovered. */
  isSelected: boolean;
  /** aria-label/title for the preview toggle button. */
  previewActionLabel: string;
  /** Invoked when the row body or the preview button is activated. */
  onSelect?: () => void;
}

/**
 * Shared chrome for a voice row in a {@link MediaRow} list: the avatar-with-preview-overlay, the
 * name, and the right-aligned action/meta grid. Used by the TTS full-page voice list
 * (`heroes/TTSFullPage/_components/VoiceRow`) and the AI Speech Classifier similar-voices list
 * (`demos/AISpeechClassifierV2/components/SimilarVoiceRow`) so the row styling stays in sync; each
 * caller supplies its own avatar source and right-aligned content via props/children.
 */
export function VoiceMediaRow({
  avatar,
  name,
  children,
  hasPreview,
  isPlaying,
  isLoading,
  progressRingProps,
  isSelected,
  previewActionLabel,
  onSelect,
}: VoiceMediaRowProps) {
  return (
    <MediaRow className='tw-relative' isSelected={isSelected} onSelect={onSelect}>
      <VoicePreviewAvatar
        avatar={avatar}
        hasPreview={hasPreview}
        isLoading={isLoading}
        isPlaying={isPlaying}
        isSelected={isSelected}
        onActivate={onSelect}
        previewActionLabel={previewActionLabel}
        progressRingProps={progressRingProps}
      />
      <p className='tw-shrink-0 tw-py-1 tw-pr-8 tw-text-black tw-type-sm'>{name}</p>
      <div className='tw-grid tw-h-9 tw-min-w-0 tw-flex-1 tw-grid-cols-1 tw-items-center tw-justify-items-end'>
        {children}
      </div>
    </MediaRow>
  );
}

/**
 * Base classes for the right-aligned action that fades in on row hover/focus. Compose with a
 * persistent-visible state (e.g. when the row is selected/playing) via clsx.
 */
export const voiceRowActionClassName = clsx(
  "tw-pointer-events-none tw-z-10 tw-col-start-1 tw-row-start-1 tw-h-9 tw-shrink-0 tw-opacity-0 tw-transition-opacity tw-duration-200 tw-ease-out",
  "group-focus-within/media-row:tw-pointer-events-auto group-focus-within/media-row:tw-opacity-100 md:group-hover/media-row:tw-pointer-events-auto md:group-hover/media-row:tw-opacity-100",
);

/**
 * Base classes for the right-aligned meta/descriptor text occupying the same grid cell as the
 * action. It's the at-rest layer the action fades in over, so callers add their own hover/focus
 * fade-out (which varies per row) plus any per-state visible/hidden classes via clsx.
 */
export const voiceRowMetaClassName =
  "tw-pointer-events-none tw-col-start-1 tw-row-start-1 tw-max-w-full tw-min-w-0 tw-truncate tw-py-1 tw-text-right tw-text-gray-500 tw-type-sm tw-transition-opacity tw-duration-200 tw-ease-out";
